export enum TransportType {
    drop,
    shuttle,
    wait,
    loaner,
    notNeeded
}

export enum TransportSubtype {
    none,
    shuttle,
    lyft,
    uber,
    enterprise
}

export enum Country {
    USA = 0,
    Canada = 1
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

export enum DMSType {
    DEFAULT = 0,
    XTIME = 1,
    CDK = 2,
    DEALERTRACK = 3,
    AUTOSOFT = 4,
    AUTOMATE = 5,
    DEALERBUILT = 6,
    REYNOLDS = 7,
    CDKEDGE = 8,
    SERTI = 12
}

/**
 * Indicates the current status of a Dealer's installation
 */
export enum DealerStatus {
    Inactive = 0,
    Active,
    PreInstall,
    Test
}

export enum AccountType {
    individual = 'individual',
    business = 'business'
}

export enum PhoneType {
    home = 0,
    cell,
    work,
    other
}

export enum QRCodeType {
    unknown = 0,
    mobileSaveSession,
    mobileComplete,
    vehicleReady,
    towIn
}

export enum SpgPricingType {
    Range = 1,
    Minimum,
    Maximum,
    NoPrice
}

export enum MaintenanceMenuType {
    Schedule = 1,
    Mileage
}

export enum Events {
    Check_In_Complete = 0,
    Check_In_Abandoned = 1,
    DetailsViewed = 2,
    Key_Loaded = 6,
    Mobile_Check_In_At_Kiosk_Started = 9,
    Locker_Open = 10,
    Customer_Dropped_Off = 13,
    ReopenedLocker = 15,
    Agreed_to_Electronic_Signatures = 18,
    Text_Opt_In = 28
}

export const Timezone = {
    0: 'America/New_York',
    1: 'America/Anchorage',
    2: 'America/Phoenix',
    3: 'America/Chicago',
    4: 'America/New_York',
    5: 'Pacific/Honolulu',
    6: 'America/Denver',
    7: 'America/Los_Angeles',
    8: 'America/Puerto_Rico',
    9: 'America/St_Johns',
} as const;

export enum SendNotificationResult {
    EmailSent = 1,
    TextSent,
    EmailAndTextSent,

    // Errors
    AppointmentNotFound,
    UnknownQRCode,
    FailedToSend,
    NotOptedInForTexts,
    OptedOutForTexts
}

// TODO: Localize for display
export enum ServiceCategory {
    'Common' = -2,
    'Common_Repairs' = -1,
    'All' = 0,
    'Air_Conditioner',
    'Air_Filtration',
    'Alignment',
    'Battery',
    'Brakes',
    'Cooling_System',
    'Drivetrain',
    'Inspection',
    'Emissions',
    'Engine',
    'Fuel_System',
    'Lights',
    'Wipers',
    'Oil',
    'Recall',
    'Suspension',
    'Tires',
    'Transmission',
    'Other',
    '5000k',
    '7500k',
    '10000k',
    '15000k',
    '20000k',
    '30000k',
    '40000k',
    '45000k',
    '60000k',
    '75000k',
    '80000k',
    '90000k',
    '100000k',
    '120000k',
    '160000k',
    'Scheduled_Service',
    'Maintenance',
    'Pci',
    'Uci_Nocert',
    'Uci_Cert'
}

export enum HunterTireResult {
    noResult = 0,
    good,
    warning,
    severe,
    danger,
    aborted,
    error,
    timeOut
}
export enum HunterPassFail {
    pass = 0,
    fail = 1
}

export enum HunterServiceStatusType {
    NoData,
    Recommended,
    Marginal,
    OK
}

export enum PaymentProvider {
    None = 0,
    StripeCanada,
    WorldPayUS
}

export enum KioskTypeName {
    VSA2 = 'VSA 2.0',
    VSA3 = 'VSA 3.0',
    VSA4 = 'VSA 4.0',
    VSAHybrid = 'VSA Hybrid',
    Cube = 'Cube',
    The247 = 'The 24/7',
    TheExpress = 'The Express',
    The365 = 'The 365',
    Mobile = 'Mobile',
}

/**
 * Indicates what kind of vehicles a dealership will temporarily give out to customers
 * that will later be returned by the customer to the Kiosk.
 */
export enum LoanerOrRentalOption {
    /**
     * The dealership is opted out of Rentals/Loaners.
     */
    None = 'None',
    /**
     * The dealership only offers Loaners and only uses Loaner functionality.
     */
    Loaners = 'Loaners',
    /**
     * The dealership only offers Rentals and only uses Rental functionality.
     */
    Rentals = 'Rentals',
    /**
     * The dealership offers both Rentals and Loaners and uses the functionality from both.
     */
    Both = 'Both'
}
/**
 * Indicates the Loaner or Rental classification assigned to a specific appointment.
 */
export enum AppointmentLoanerOrRentalOption {
    /**
     * The appointment has no loaner or rental associated.
     */
    None = 'None',
    /**
     * The customer has been assigned a loaner vehicle.
     */
    Loaner = 'Loaner',
    /**
     * The customer has been assigned a rental vehicle.
     */
    Rental = 'Rental'
}

// TODO: These should be capitalized like all our other enums
export enum AppointmentType {
    none,
    checkedIn,
    created,
    dmsAppointment,
    priority,
    keyDroppedOff,
    inKeyBin,
    rentalOrLoanerInBin
}

export enum DateRangeKey {
    none,
    today,
    yesterday,
    thisWeek,
    lastWeek,
    thisMonth
}

/**
 * Determines which valuation screen layout the kiosk displays to the customer.
 */
export enum ValuationScreen {
    /**
     * No valuation screen is shown.
     */
    None = 0,
    /**
     * Standard Blackbook trade-in valuation screen.
     */
    Blackbook = 1,
    /**
     * Equity-detected screen highlighting positive equity.
     */
    EquityDetected = 2,
    /**
     * BMW Honolulu dealer-specific value splash layout.
     */
    ValueSplash_BMWHONOLULU = 3,
    /**
     * MINI Hawaii dealer-specific value splash layout.
     */
    ValueSplash_MINIHAWAII = 4,
}

/**
 * Determines which valuation screen layout the mobile experience displays.
 */
export enum MobileValuationScreen {
    /**
     * Standard Blackbook trade-in valuation screen.
     */
    Blackbook = 1,
    /**
     * Equity-detected screen highlighting positive equity.
     */
    EquityDetected = 2,
}

/**
 * The method used to adjust Blackbook valuation amounts for a dealer.
 */
export enum BlackbookAdjustmentMethod {
    /**
     * Adjustment is applied as a percentage of the raw value.
     */
    Percentage = 1,
    /**
     * Adjustment is applied as a fixed dollar amount.
     */
    FlatRate = 2,
}